/**
 * ManualSyncTab — a first-class institution UX to bulk-update students to
 * any pipeline stage via an Excel/CSV upload. One drop-zone per stage; each
 * card shows its own result box so the institution can see exactly how many
 * matched, how many were already at/past the target, and which rows failed
 * to tally with a name/email.
 *
 * Everything the single-student action buttons do (bell + email + WS push,
 * stage_history audit, per-stage timestamps) is wired through the same
 * `POST /api/institution/students/sync-coe-file` endpoint — which despite
 * its historical name accepts every institution-visible stage.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/config/api';
import {
  UploadSimple, CircleNotch, CheckCircle, PaperPlaneTilt, MagnifyingGlass,
  ShieldCheck, HandCoins, GraduationCap, XCircle, CurrencyDollar, FileText,
} from '@phosphor-icons/react';

// Stage list mirrors PIPELINE_STAGES on backend (minus profile_created,
// which is auto-assigned to new students and never bulk-set).
const STAGE_CARDS = [
  {
    key: 'application_sent',
    label: 'Application Sent',
    hint: 'Students the recruiter has forwarded to your college.',
    icon: PaperPlaneTilt,
    accent: 'bg-slate-100 text-slate-800 border-slate-200',
    btn: 'bg-slate-700 hover:bg-slate-800',
  },
  {
    key: 'docs_under_review',
    label: 'Docs Under Review',
    hint: 'Students whose documents your team is currently reviewing.',
    icon: MagnifyingGlass,
    accent: 'bg-amber-50 text-amber-900 border-amber-200',
    btn: 'bg-amber-600 hover:bg-amber-700',
  },
  {
    key: 'docs_verified',
    label: 'Docs Verified',
    hint: 'Students whose documents you have verified as acceptable.',
    icon: ShieldCheck,
    accent: 'bg-teal-50 text-teal-900 border-teal-200',
    btn: 'bg-teal-600 hover:bg-teal-700',
  },
  {
    key: 'enrollment_agreement_sent',
    label: 'EA Sent',
    hint: 'Students to whom you have sent their Enrollment Agreement.',
    icon: FileText,
    accent: 'bg-amber-50 text-amber-900 border-amber-200',
    btn: 'bg-amber-700 hover:bg-amber-800',
  },
  {
    key: 'enrollment_agreement_signed',
    label: 'EA Signed',
    hint: 'Students who have signed their Enrollment Agreement.',
    icon: CheckCircle,
    accent: 'bg-yellow-50 text-yellow-900 border-yellow-200',
    btn: 'bg-yellow-600 hover:bg-yellow-700',
  },
  {
    key: 'half_money_received',
    label: '1/2 Money Received',
    hint: 'First tuition tranche received. Financials are NOT set from this — use the Mark 1/2 Money button on the student.',
    icon: HandCoins,
    accent: 'bg-lime-50 text-lime-900 border-lime-200',
    btn: 'bg-lime-600 hover:bg-lime-700',
  },
  {
    key: 'coe_sent',
    label: 'COE Sent',
    hint: 'Confirmation of Enrollment issued to the student.',
    icon: GraduationCap,
    accent: 'bg-blue-50 text-blue-900 border-blue-200',
    btn: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    hint: 'Students you cannot accept. Use with care — recruiters get notified.',
    icon: XCircle,
    accent: 'bg-red-50 text-red-900 border-red-200',
    btn: 'bg-red-600 hover:bg-red-700',
  },
];

function StageUploadCard({ stage, onSynced }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const Icon = stage.icon;

  const runSync = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('stage', stage.key);
      const { data } = await axios.post(`${API}/institution/students/sync-coe-file`, fd);
      setResult(data);
      toast.success(`${stage.label}: ${data.updated} student(s) updated`);
      onSynced?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ''; };

  return (
    <div className={`rounded-xl border ${stage.accent} p-4 flex flex-col gap-3`} data-testid={`manual-sync-card-${stage.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white/70 border border-white/60 flex items-center justify-center">
            <Icon size={18} weight="fill" />
          </div>
          <div>
            <h3 className="text-sm font-bold leading-tight">{stage.label}</h3>
          </div>
        </div>
      </div>
      <p className="text-[11px] leading-snug opacity-80">{stage.hint}</p>

      {!result && (
        <div className="mt-auto space-y-2">
          <label className="block cursor-pointer">
            <span className="sr-only">Choose file</span>
            <Input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="text-[11px] bg-white/70"
              data-testid={`manual-sync-file-${stage.key}`}
            />
          </label>
          {file && <p className="text-[10px] truncate opacity-80">Selected: <strong>{file.name}</strong></p>}
          <Button
            onClick={runSync}
            disabled={!file || busy}
            className={`w-full text-white ${stage.btn}`}
            data-testid={`manual-sync-submit-${stage.key}`}
          >
            {busy
              ? <><CircleNotch size={14} className="mr-2 animate-spin" /> Syncing…</>
              : <><UploadSimple size={14} className="mr-2" /> Upload & mark as {stage.label}</>}
          </Button>
        </div>
      )}

      {result && (
        <div className="mt-auto space-y-2" data-testid={`manual-sync-result-${stage.key}`}>
          <div className="grid grid-cols-3 gap-1.5 text-[10px]">
            <div className="rounded-md bg-white/70 border border-white/60 p-1.5 text-center">
              <p className="opacity-70">Updated</p>
              <p className="text-lg font-bold tabular-nums leading-tight">{result.updated}</p>
            </div>
            <div className="rounded-md bg-white/70 border border-white/60 p-1.5 text-center">
              <p className="opacity-70">Already ≥</p>
              <p className="text-lg font-bold tabular-nums leading-tight">{result.already_at_stage}</p>
            </div>
            <div className="rounded-md bg-white/70 border border-white/60 p-1.5 text-center">
              <p className="opacity-70">Unmatched</p>
              <p className="text-lg font-bold tabular-nums leading-tight">{result.unmatched}</p>
            </div>
          </div>
          {Array.isArray(result.unmatched_rows) && result.unmatched_rows.length > 0 && (
            <details className="rounded-md bg-white/70 border border-white/60 text-[10px]">
              <summary className="cursor-pointer px-2 py-1 font-semibold">Show unmatched ({result.unmatched_rows.length})</summary>
              <ul className="border-t border-white/60 max-h-32 overflow-y-auto px-2 py-1 space-y-0.5">
                {result.unmatched_rows.map((r, i) => (
                  <li key={i} className="truncate">{r.name || '—'} · <span className="opacity-70">{r.email || '—'}</span></li>
                ))}
              </ul>
            </details>
          )}
          <Button variant="ghost" size="sm" onClick={reset} className="w-full text-[11px] h-7" data-testid={`manual-sync-reset-${stage.key}`}>
            Upload another sheet
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ManualSyncTab({ onAnyUploadDone }) {
  return (
    <div className="space-y-6" data-testid="manual-sync-tab">
      <header className="rounded-xl bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <CurrencyDollar size={18} className="text-emerald-600" />
          <h2 className="text-lg font-bold" style={{ fontFamily: "'Libre Baskerville', serif" }}>Manual Sync</h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
          Upload an Excel/CSV of students for any pipeline stage. We&rsquo;ll match rows by <strong>email</strong> or <strong>name</strong>
          (or <em>First Name</em> + <em>Last Name</em>) against students at your college and flip each match to the chosen stage.
          Recruiters and admins get instant bell + email notifications for every student updated. The sync <strong>never moves students backwards</strong> —
          if someone is already at or past the target, they&rsquo;re counted as &ldquo;already ≥&rdquo; and left untouched.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAGE_CARDS.map(s => (
          <StageUploadCard key={s.key} stage={s} onSynced={onAnyUploadDone} />
        ))}
      </div>
    </div>
  );
}
